import type {WebCommitInfo} from '@github-ui/code-view-types'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useNavigate} from '@github-ui/use-navigate'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {AlertIcon, GitBranchIcon, LockIcon, PencilIcon} from '@primer/octicons-react'
import {Button, Link} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'

import styles from './EditIssues.module.css'

export function EditIssues({
  binary,
  helpUrl,
  webCommitInfo,
}: {
  binary: boolean
  helpUrl: string
  webCommitInfo: WebCommitInfo
}) {
  const {shouldFork, lockedOnMigration, shouldUpdate, userOverRepositoryLimit} = webCommitInfo
  let pathname = ssrSafeLocation.pathname

  // Slice trailing slash because the csrf token is generated without it
  if (pathname.endsWith('/')) {
    pathname = pathname.slice(0, -1)
  }

  const navigate = useNavigate()
  const {addToast} = useToastContext()
  const currentPath = pathname + ssrSafeLocation.search

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const response = await verifiedFetch(currentPath, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      })

      if (response.ok) {
        // reload the page to reflect the fork/update
        navigate(currentPath)
      } else {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: 'An unexpected error occurred. Please try again.',
        })
      }
    } catch {
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: 'error',
        message: 'An unexpected error occurred. Please try again.',
      })
    }
  }

  const issue = lockedOnMigration
    ? {
        message: 'This repository is currently being migrated.',
        description: 'Sorry, you’re not able to edit this repository while the migration is in progress.',
        icon: LockIcon,
      }
    : shouldFork
      ? userOverRepositoryLimit
        ? {
            message: 'You are over your repository limit',
            description: 'Sorry, you’re not able to fork this repository.',
            icon: AlertIcon,
          }
        : {
            message: 'You need to fork this repository to propose changes.',
            description:
              'Sorry, you’re not able to edit this repository directly—you need to fork it and propose your changes from there instead.',
            icon: GitBranchIcon,
          }
      : shouldUpdate
        ? {
            message: 'Sorry, it looks like your fork is outdated!',
            description: 'You’ll have to bring it up to date before you can propose changes.',
            icon: AlertIcon,
          }
        : binary
          ? {
              message: 'Binary file content is not editable.',
              description: 'But you can still rename or move it.',
              icon: PencilIcon,
            }
          : null

  if (!issue) return null

  return (
    <div className={styles.Box}>
      <Octicon icon={issue.icon} size="medium" className={styles.Octicon} />
      <h3 className={styles.Text}>{issue.message}</h3>
      <span className={styles.Text_1}>{issue.description}</span>
      {((shouldFork && !userOverRepositoryLimit) || shouldUpdate) && (
        <form
          onSubmit={handleSubmit}
          data-turbo="false"
          method="post"
          action={currentPath}
          data-testid="edit-issues-form"
        >
          {/** Sumbit the page as a POST request so we can fork the repository for the user **/}
          <Button type="submit" variant="primary">
            {shouldFork ? 'Fork this repository' : 'Update your fork'}
          </Button>
        </form>
      )}
      {shouldFork && userOverRepositoryLimit ? (
        <Link href={`${helpUrl}/repositories/creating-and-managing-repositories/repository-limits`}>
          Learn more repository limits
        </Link>
      ) : (
        <Link href={`${helpUrl}/articles/fork-a-repo`}>Learn more about forks</Link>
      )}
    </div>
  )
}
